import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { useIsLoggedIn } from '@renderer/hooks/useIsLoggedIn'
import { useForYouUnreadCount } from '@renderer/overlay/hooks/use-for-you-unread-count'
import { AudioNoteStartingButton } from './audio-note/audio-note-starting-button'
import { AudioNoteWaveformButton } from './audio-note/audio-note-waveform-button'
import { TranscriptPreviewButton } from './audio-note/transcript-preview-button'
import type { AudioNotePhase } from './audio-note/use-audio-note-phase'
import { useIsMiniContentHovered, useIsMiniRevealed, useSetMiniOpenSurface } from './atoms'
import { Mini } from './components/mini'
import type { MiniNotchItem } from './components/mini/notch'
import { useMiniNotchRegion } from './components/mini/notch/notch-region'
import { MAX_REDESIGN_NOTCH_WIDTH } from './components/mini/notch'
import { MiniNotchControls } from './components/mini/nested/controls'
import { MiniFinishedControlsProvider } from './components/mini/nested/finished-controls'
import { MiniAskButton } from './components/mini/nested/ask-button'
import { ForYouChip } from './components/mini/nested/for-you-chip'
import { IncognitoChip } from './components/mini/nested/incognito-chip'
import { MiniMenuButton } from './components/mini/nested/mini-menu'
import { MiniNewMeetingButton } from './components/mini/nested/new-meeting-button'
import { MiniAskInput } from './mini-ask-input'
import {
  CapturedActionList,
  CapturedActionsChip,
  EndedMeetingStatus,
  FinishedMeetingTitle,
  useCapturedActions,
  useOpenEndedMeeting,
} from './components/mini/nested/captured-actions'
import { trackNotchAction } from './track-notch'
import { SetupCallLabelEar, SetupCallLeadEar } from './setup-call/nested/ears'
import { SetupCallPanel } from './setup-call/setup-call-panel'
import { SETUP_CALL_CORNER_RADIUS, SETUP_CALL_FACE_WIDTH } from './setup-call/nested/face'
import type { SetupCallOffer } from './setup-call/use-setup-call-offer'

const COLLAPSE_IDLE_MINI_BEHIND_NOTCH = true
const MINI_ASK_SURFACE_ID = 'mini-ask'
/** The post-meeting notch clears this long after its action items are ready, unless hovered, or when the sidepanel closes. */
const FINISHED_NOTCH_MS = 10_000

interface MiniNotchedProps {
  onLogoClick: () => void
  phase: AudioNotePhase
  setupCall: SetupCallOffer
}

export const MiniNotched = memo(function MiniNotched({ onLogoClick, phase, setupCall }: MiniNotchedProps) {
  const isContentHovered = useIsMiniContentHovered()
  const isRevealed = useIsMiniRevealed()
  const setOpenSurface = useSetMiniOpenSurface()
  const { hasNotch, width: notchWidth } = useMiniNotchRegion()
  const unreadCount = useForYouUnreadCount()
  const isLoggedIn = useIsLoggedIn()
  const { isIncognitoEnabled, disableIncognito, incognitoTimer } = useIncognitoMode()
  const showsCapturedActions = phase === 'recording'
  const showsEndedMeeting = phase === 'saving' || phase === 'finished'
  const captured = useCapturedActions(phase, isContentHovered)
  const capturedCount = captured.items.length
  const openEndedMeeting = useOpenEndedMeeting()
  const [isAskInputExpanded, setIsAskInputExpanded] = useState(false)

  const isAskExpanded = phase === 'idle' && isAskInputExpanded
  const setupCallMoment = phase === 'idle' ? setupCall.moment : null
  const isSetupCallOpen = setupCallMoment !== null && setupCall.open !== null
  const showSetupCallInvite = useCallback(() => {
    trackNotchAction('setup call', { step: 'ears clicked', moment: setupCallMoment })
    setupCall.show('invite')
  }, [setupCall, setupCallMoment])
  // The booking takes the row Ask lives in, so an expanded Ask closes rather than stacking under it
  useEffect(() => {
    if (isSetupCallOpen) setIsAskInputExpanded(false)
  }, [isSetupCallOpen])
  const toggleAskInput = useCallback(() => {
    if (!isAskExpanded) trackNotchAction('ask opened', { display: hasNotch ? 'notch' : 'stand-in' })
    setIsAskInputExpanded(!isAskExpanded)
  }, [hasNotch, isAskExpanded])

  const handleDisableIncognito = useCallback(() => disableIncognito('mini-notch'), [disableIncognito])

  useEffect(() => {
    setOpenSurface({ id: MINI_ASK_SURFACE_ID, open: isAskExpanded })
  }, [isAskExpanded, setOpenSurface])

  useEffect(() => {
    return () => setOpenSurface({ id: MINI_ASK_SURFACE_ID, open: false })
  }, [setOpenSurface])

  useEffect(() => {
    if (phase !== 'idle') {
      setIsAskInputExpanded(false)
    }
  }, [phase])

  // The meeting row shows its time and controls while hovered, and while a new action's list is up
  const isMeetingRowExpanded = phase === 'recording' && (isRevealed || captured.isListShown)
  // Each side's share of the notch's width cap, less the padding around its name (and icon, which scrolls with it)
  const sideRoom = Math.max(0, (MAX_REDESIGN_NOTCH_WIDTH - notchWidth) / 2)
  const nameWidth = Math.max(40, sideRoom - 20)

  const items = useMemo<Array<MiniNotchItem>>(
    () => [
      // `left` renders reversed, so its items are listed from the notch outward
      {
        id: 'new-meeting',
        slot: 'left',
        show: phase === 'idle' && isRevealed && isLoggedIn && !isSetupCallOpen,
        node: <MiniNewMeetingButton />,
      },
      {
        id: 'ask-toggle',
        slot: 'left',
        show: phase === 'idle' && !isSetupCallOpen,
        node: <MiniAskButton isOpen={isAskExpanded} onToggle={toggleAskInput} />,
      },
      {
        id: 'setup-call-lead',
        slot: 'left',
        show: isSetupCallOpen,
        node: <SetupCallLeadEar moment={setupCallMoment ?? 'first'} onOpen={showSetupCallInvite} />,
      },
      {
        id: 'starting',
        slot: 'left',
        show: phase === 'starting',
        node: <AudioNoteStartingButton />,
      },
      {
        // The redesign's meeting rests on the waveform and its action count, and spells both out while hovered
        id: 'captured-actions',
        slot: 'left',
        show: showsCapturedActions && capturedCount > 0,
        node: (
          <CapturedActionsChip count={capturedCount} newCount={captured.newCount} isExpanded={isMeetingRowExpanded} />
        ),
      },
      {
        id: 'waveform',
        slot: 'right',
        show: phase === 'recording',
        node: <AudioNoteWaveformButton showElapsed={isMeetingRowExpanded} />,
      },
      {
        // The stand-in has no notch to avoid, so its middle shows what's being said, at the stand-in's own width.
        // Mounted for the whole recording so the row's layout holds still between utterances.
        id: 'transcript-preview',
        slot: 'center',
        show: phase === 'recording' && !hasNotch,
        style: { width: notchWidth },
        node: <TranscriptPreviewButton />,
      },
      {
        id: 'recording-pause-stop',
        slot: 'right',
        show: isMeetingRowExpanded,
        node: <MiniNotchControls />,
      },
      {
        // The ended meeting keeps its name left and its drafting status right from saving through finished
        id: 'ended-meeting-title',
        slot: 'left',
        show: showsEndedMeeting,
        node: <FinishedMeetingTitle width={nameWidth} />,
      },
      {
        id: 'ended-meeting-status',
        slot: 'right',
        show: showsEndedMeeting,
        node: <EndedMeetingStatus isDrafting={captured.isDrafting} hasActions={capturedCount > 0} />,
      },
      {
        id: 'ask-highlight',
        slot: 'below',
        show: isAskExpanded,
        className: 'w-full',
        // The capped notch sets the width, and the input fills it inside the row's padding
        node: <MiniAskInput onExpandedChange={setIsAskInputExpanded} />,
      },
      {
        id: 'incognito',
        slot: 'right',
        show: isIncognitoEnabled,
        node: <IncognitoChip incognitoTimer={incognitoTimer} onDisable={handleDisableIncognito} />,
      },
      {
        id: 'captured-list',
        slot: 'below',
        show: captured.isListShown,
        className: 'w-full',
        hasOwnEntrance: true,
        node: <CapturedActionList items={captured.listed} label={null} />,
      },
      {
        id: 'setup-call-label',
        slot: 'right',
        show: setupCallMoment !== null,
        node: <SetupCallLabelEar moment={setupCallMoment ?? 'first'} onOpen={showSetupCallInvite} />,
      },
      {
        id: 'setup-call-panel',
        slot: 'below',
        show: isSetupCallOpen,
        width: SETUP_CALL_FACE_WIDTH,
        cornerRadius: setupCall.open ? SETUP_CALL_CORNER_RADIUS : undefined,
        node: <SetupCallPanel offer={setupCall} />,
      },
      {
        id: 'for-you',
        slot: 'right',
        show: phase === 'idle' && !isSetupCallOpen,
        node: <ForYouChip count={unreadCount} />,
      },
      {
        id: 'menu',
        slot: 'right',
        // A meeting's notch has no room for it, from starting through its finished state
        show: isRevealed && phase === 'idle' && !isSetupCallOpen,
        node: <MiniMenuButton />,
      },
    ],
    [
      phase,
      isAskExpanded,
      toggleAskInput,
      showsCapturedActions,
      showsEndedMeeting,
      capturedCount,
      captured.newCount,
      captured.listed,
      captured.isListShown,
      captured.isDrafting,
      nameWidth,
      isIncognitoEnabled,
      incognitoTimer,
      handleDisableIncognito,
      unreadCount,
      isRevealed,
      isMeetingRowExpanded,
      isLoggedIn,
      hasNotch,
      notchWidth,
      setupCall,
      setupCallMoment,
      isSetupCallOpen,
      showSetupCallInvite,
    ],
  )

  const isCollapsed = COLLAPSE_IDLE_MINI_BEHIND_NOTCH && phase === 'idle' && !isRevealed && setupCallMoment === null

  // Mounted in every phase: swapping the notch in and out of the provider would remount it, skipping its animations
  return (
    <MiniFinishedControlsProvider
      isActive={phase === 'finished'}
      autoCollapse={{
        delayMs: FINISHED_NOTCH_MS,
        isHeld: captured.isDrafting || isContentHovered,
        onSidepanelClose: true,
      }}
    >
      <Mini.Notch
        isCollapsed={isCollapsed}
        items={items}
        onLogoClick={onLogoClick}
        // A recording meeting's notch drops its action items down when clicked; an ended one opens the saved meeting
        onClick={
          showsEndedMeeting
            ? () => openEndedMeeting(captured.isDrafting ? 'drafting' : 'notch click')
            : showsCapturedActions && captured.canOpen
              ? captured.open
              : undefined
        }
        // A recording notch collapses when not hovered, so only an ending meeting holds its width
        holdsWidth={showsEndedMeeting}
      />
    </MiniFinishedControlsProvider>
  )
})
